import { FileArchive, Send, Trash2 } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import type { BulkTask } from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import { ChoiceCards } from '../../../ui/Choice';
import styles from './Batch.module.css';
import { taskTitle } from './text';

/**
 * Step 1, the task (BLOCK-1B-BULK-DESIGN §2): choice cards with imperative titles and a one-line
 * description, only the tasks the user may run (a viewer sees the PDF card only). Never a row of
 * buttons beside the batch (AC-B5).
 */
export function TaskStep({
  tasks,
  value,
  onChange,
  isDisabled,
}: {
  /** The tasks this user may run, in order. */
  tasks: readonly BulkTask[];
  value: BulkTask | null;
  onChange: (task: BulkTask) => void;
  isDisabled?: boolean;
}) {
  const { t } = useI18n();
  const legend = useId();
  const hints: Record<BulkTask, string> = {
    send: t.batch.tasks.sendHint,
    pdf: t.batch.tasks.pdfHint,
    discard: t.batch.tasks.discardHint,
  };
  const icons: Record<BulkTask, ReactNode> = {
    send: <Send size={18} />,
    pdf: <FileArchive size={18} />,
    discard: <Trash2 size={18} />,
  };
  return (
    <div className={styles.block}>
      <p id={legend} className={styles.legend}>
        {t.batch.tasks.legend}
      </p>
      <ChoiceCards<BulkTask>
        aria-labelledby={legend}
        value={value !== null && tasks.includes(value) ? value : null}
        onChange={onChange}
        isDisabled={isDisabled ?? false}
        testId="batch-tasks"
        options={tasks.map((task) => ({
          value: task,
          title: taskTitle(t, task),
          description: hints[task],
          icon: icons[task],
          testId: `batch-task-${task}`,
        }))}
      />
    </div>
  );
}
